/**
 * Card "products" derivation (NPJB-409). Derives the displayed product list for
 * an audience — and the union for a campaign — from the raw `product_selections`
 * + `product_answers` the listing APIs now return. Runs entirely on the FE so
 * the backend no longer aggregates product names per request.
 *
 * Self-contained (no `@/app` / store imports) so it can live in `src/lib` and be
 * shared by home, campaigns-home, and campaign-detail.
 *
 * Sub-option ids below mirror the backend catalog + the `EMAIL_NURTURE_*`
 * constants in `dzone-backend/app/application/audience/request_schemas.py` — keep
 * them in sync if catalog option ids change.
 */

/** Canonical display order (matches the Products screen diagram). */
export const PRODUCT_KEYS = [
  'third-eye',
  'programmatic-nurture',
  'lead-gen',
  'opt-in',
  'email-blasts',
  'display',
  'editorial',
  'dedicated-newsletter',
  'sponsored-newsletter',
  'newsletter',
  'insights-pod',
  'insights-hub',
  'content',
] as const;

export type ProductKey = (typeof PRODUCT_KEYS)[number];

export const PRODUCT_LABEL: Record<ProductKey, string> = {
  'third-eye': '3rd Eye',
  'programmatic-nurture': 'Programmatic Nurture',
  'lead-gen': 'Lead Gen',
  'opt-in': 'Opt In',
  'email-blasts': 'Email Blasts',
  display: 'Display',
  editorial: 'Editorial',
  'dedicated-newsletter': 'Dedicated Newsletter',
  'sponsored-newsletter': 'Sponsored Newsletter',
  newsletter: 'Newsletter',
  'insights-pod': 'Insights Pod',
  'insights-hub': 'Insights Hub',
  content: 'Content',
};

type ProductAnswerValue = string | string[] | null;
/** `{ productSlug: { fieldId: value } }` — as returned by the listing APIs. */
export type ProductAnswersMap = Record<string, Record<string, ProductAnswerValue>>;
/** Slug is canonical post-NPJB-298; legacy rows fall back to `name`. */
export interface ProductSelectionInput {
  slug?: string | null;
  name?: string | null;
}

export interface AudienceProductInputs {
  productSelections: ProductSelectionInput[];
  productAnswers: ProductAnswersMap;
  /**
   * Did the marketer ask Digitalzone to BUILD content for them? (NPJB-644)
   *
   * This is the Build Content picker on the wizard Content step, stored as
   * `audience_requests.content_products` and surfaced as `contentProductCount`
   * on the list payloads. It is the ONLY thing that makes 'content' a product.
   *
   * Attaching a file or a link is not a content request — it is the marketer
   * handing us an asset they already own. This field used to be `hasContent`,
   * fed from the link count, which made a single pasted URL look like a
   * content-as-a-service order and hid every real one.
   */
  hasRequestedContent: boolean;
}

/** Normalise a catalog answer to a string list. Shared with `./content-step.ts`. */
export const asStringArray = (v: ProductAnswerValue | undefined): string[] => {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  return typeof v === 'string' && v ? [v] : [];
};

const isFilled = (v: ProductAnswerValue | undefined): boolean =>
  Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== '';

/** Top-level product slug → key. */
const SLUG_TO_KEY: Record<string, ProductKey> = {
  '3rd-eye': 'third-eye',
  'prog-nurture': 'programmatic-nurture',
  'lead-gen': 'lead-gen',
  display: 'display',
  editorial: 'editorial',
};

/** Legacy fallback for pre-NPJB-298 rows whose selections lack a `slug`. */
const NAME_FALLBACK: readonly { match: RegExp; key: ProductKey }[] = [
  { match: /programmatic nurture/i, key: 'programmatic-nurture' },
  { match: /lead gen/i, key: 'lead-gen' },
  { match: /display/i, key: 'display' },
  { match: /editorial/i, key: 'editorial' },
];

/** NPJB-459 two-level editorial: product-type option → card key. */
const EDITORIAL_TYPE_TO_KEY: Record<string, ProductKey> = {
  'ed-dedicated-nl': 'dedicated-newsletter',
  'ed-sponsored-nl': 'sponsored-newsletter',
  'ed-insights-pod': 'insights-pod',
  'ed-insights-hub': 'insights-hub',
};

/** Legacy (pre-NPJB-459) flat `ed-products` options that imply Insights Hub. */
const INSIGHTS_HUB_OPTIONS = new Set([
  'ed-cto-insights',
  'ed-adpulse-hub',
  'ed-ctomag-hub',
  'ed-digdig-hub',
]);

/** Editorial derivation — new two-level types (NPJB-459) plus the legacy
 *  flat `ed-products` fallback for pre-NPJB-459 rows. */
const addEditorialKeys = (
  present: Set<ProductKey>,
  productAnswers: ProductAnswersMap,
  editorialTypes: string[],
): void => {
  for (const type of editorialTypes) {
    const key = EDITORIAL_TYPE_TO_KEY[type];
    if (key) present.add(key);
  }

  const legacyEditorial = asStringArray(productAnswers?.editorial?.['ed-products']);
  if (
    editorialTypes.length === 0 &&
    (legacyEditorial.includes('ed-newsletter') ||
      isFilled(productAnswers?.editorial?.['email-send-goal']))
  )
    present.add('newsletter');
  if (legacyEditorial.some((opt) => INSIGHTS_HUB_OPTIONS.has(opt))) present.add('insights-hub');
};

/** Products for a single audience, in canonical order. */
export const deriveAudienceProducts = ({
  productSelections,
  productAnswers,
  hasRequestedContent,
}: AudienceProductInputs): ProductKey[] => {
  const present = new Set<ProductKey>();
  const editorialTypes = asStringArray(productAnswers?.editorial?.['ed-product-types']);

  for (const sel of productSelections ?? []) {
    const slug = sel?.slug?.trim().toLowerCase();
    const bySlug = slug ? SLUG_TO_KEY[slug] : undefined;
    const key = bySlug ?? NAME_FALLBACK.find((f) => f.match.test(sel?.name ?? ''))?.key;
    if (!key) continue;
    // New-model editorial rows list the selected product types instead of the
    // generic Editorial label; rows without types (legacy / bare drafts) keep it.
    if (key === 'editorial' && editorialTypes.length > 0) continue;
    present.add(key);
  }

  const leadGen = asStringArray(productAnswers?.['lead-gen']?.['lg-products']);
  if (leadGen.includes('lg-opt-in')) present.add('opt-in');
  if (leadGen.includes('lg-email-blasts')) present.add('email-blasts');

  addEditorialKeys(present, productAnswers, editorialTypes);

  if (hasRequestedContent) present.add('content');

  return PRODUCT_KEYS.filter((k) => present.has(k));
};

/** Union of every child audience's products, in canonical order. */
export const deriveCampaignProducts = (
  children: readonly AudienceProductInputs[],
): ProductKey[] => {
  const present = new Set<ProductKey>();
  for (const child of children ?? []) {
    for (const key of deriveAudienceProducts(child)) present.add(key);
  }
  return PRODUCT_KEYS.filter((k) => present.has(k));
};

export const deriveAudienceProductLabels = (inputs: AudienceProductInputs): string[] =>
  deriveAudienceProducts(inputs).map((k) => PRODUCT_LABEL[k]);

export const deriveCampaignProductLabels = (children: readonly AudienceProductInputs[]): string[] =>
  deriveCampaignProducts(children).map((k) => PRODUCT_LABEL[k]);
